import { BreakpointObserver } from '@angular/cdk/layout';
import { CommonModule, DOCUMENT, isPlatformBrowser, isPlatformServer } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Inject, Input, Output, PLATFORM_ID, ViewChild } from '@angular/core';
import { JobBodyPageComponent, TypographicalHeaderPageComponent, Video, VideoPageComponent } from '@interfaces';
import { GtagService } from 'src/app/containers/gtag/gtag.service';
import { VideosService } from 'src/app/rest/videos.service';
import { ComponentActionsService } from '../../services/component-actions.service';
import { AbstractVideoComponent } from '../abstract-video-component';

@Component({
    selector: 'app-video',
    templateUrl: './video.component.html',
    standalone: true,
    imports: [CommonModule],
})
export class VideoComponent extends AbstractVideoComponent implements AfterViewInit {
    @Input() public component: VideoPageComponent | TypographicalHeaderPageComponent | JobBodyPageComponent;
    @Input() public video: string;
    @Output() public videoLoaded = new EventEmitter<void>();
    public showPlaylist = false;
    public showInternalVideo = false;
    public showLoomVideo = false;
    public showYouTubeVideo = false;
    @ViewChild("videoElement", { read: ElementRef, static: false }) public videoElement: ElementRef;
    @ViewChild("loom", { read: ElementRef, static: false }) public loom: ElementRef;
    @ViewChild("youtube", { read: ElementRef, static: false }) public youtube: ElementRef;

    constructor(
        protected videosService: VideosService,
        protected actionsService: ComponentActionsService,
        @Inject(PLATFORM_ID) protected platform,
        @Inject(DOCUMENT) protected document,
        protected breakpointObserver: BreakpointObserver,
        protected gtag: GtagService,
        private changeDetectorRef: ChangeDetectorRef,
    ) {
        super(videosService, actionsService, platform, document, breakpointObserver, gtag);
    };

    ngOnInit() {

        if(this.component.source === 'loom' && this.component.videoUrl) {
            this.showLoomVideo = true;
        } else if(this.component.source === 'youtube' && this.component.videoUrl) {
            this.showYouTubeVideo = true;
        } else if(this.component.source === 'job' && this.job?.videoSource === 'loom' && this.job.videoUrl) {
            this.showLoomVideo = true;
        } else if(this.component.source === 'job' && this.component.video) {
            this.showInternalVideo = true;
        } else if (this.component.source === 'job' && this.job.videoSource === 'custom' && this.job.video) {
            this.showInternalVideo = true;
        } else if(this.component.video) {
            this.showInternalVideo = true;
        } else if(this.video) {
            this.showInternalVideo = true;
        }

        if (isPlatformServer(this.platform)) {
            let video;

            switch(this.component.source) {
                case 'youtube':
                case 'loom':
                    break;
                case 'job':
                    if(this.job?.videoSource === 'loom') {
                       break;
                    } else {
                        video = this.job.video;
                    }
                    break;
                case 'custom':
                default:
                    video = this.video || this.component.video;
                    break;
            }

            if(video) {
                this.videosService.resolveVideo(video).subscribe(() => {});
            }

        }
    }

    async ngAfterViewInit() {
        if (isPlatformBrowser(this.platform)) {
            let video;

            switch(this.component.source) {
                case 'loom':
                    const { oembed } = await this.importLoom();

                    if(this.component.videoUrl) {
                        const loom = await oembed(this.component.videoUrl);
                        this.loom.nativeElement.innerHTML = loom.html;
                    }
                    break;
                case 'youtube':
                    if(this.component.videoUrl) {
                        this.youtube.nativeElement.innerHTML = `<iframe width="100%" height="100%" src="${this.component.videoUrl}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>`;
                    }
                    break;
                case 'job':
                    if(this.job?.videoSource === 'loom') {
                        if(this.job.videoUrl) {
                            const { oembed } = await this.importLoom();
                            const loom = await oembed(this.job.videoUrl);
                            this.loom.nativeElement.innerHTML = loom.html;
                        }
                     } else {
                         video = this.job.video;
                     }
                    break;
                case 'custom':
                default:
                    video = this.video || this.component.video;
                    break;
            }
            if(video) {
                this.videosService.resolveVideo(video).subscribe(
                    (video: Video) => {
                        this.loading = false;
                        this.subtitles = video.subtitles;
                        this.playlist = video.playlistData;
                        this.originVideo = video;
                        this.videoData = video;
                        this.loadVideo(this.videoData);
                        this.changeDetectorRef.detectChanges();
                    },
                    (error) => {
                        this.loading = false;
                        this.error = true;
                        this.changeDetectorRef.detectChanges();
                    }
                );
            }

        }
    }

    private async importLoom(): Promise<any> {
        return await import("@loomhq/loom-embed");
    }

    public togglePlaylist() {
        this.showPlaylist = !this.showPlaylist;
    }

    public playPlaylistVideo(video: Video) {
        this.showPlaylist = false;
        this.paused = false;
        this.unloadSource();
        this.loadVideo(video);
    }

    public loadVideo(video: Video) {
        super.loadVideo(video);
        if(this.component.playlist) {
            this.videoElement.nativeElement.addEventListener('ended', () => this.showPlaylist = true, true);
        }
        this.videoLoaded.emit();
    }
}
